import { FeedItem } from "../types/feed";
import { ServerStatus } from "../services/serversService";
export interface ServerStat {
  name: string;
  playerCount: number | null;
  isOnline: boolean;
}

export interface WeeklyStats {
  activePilots: number;
  killCount: number;
  flightHours: number;
  proficienciesEarned: number;
  achievementsEarned: number;
  servers: ServerStat[];
  topPilot: { uid: string; name: string; killCount: number; flightHours: number } | null;
}

const SERVER_LIST_MAX = 4;

export function computeWeeklyStats(items: FeedItem[], servers: ServerStatus[], since: Date): WeeklyStats {
  const recentItems = items.filter((item) => {
    const d = new Date(item.occurred_at);
    return !Number.isNaN(d.getTime()) && d >= since;
  });

  const uniquePilots = new Set<string>();
  let killCount = 0;
  let flightSeconds = 0;
  let proficienciesEarned = 0;
  let achievementsEarned = 0;
  const pilotCounts = new Map<string, { uid: string; name: string; count: number; kills: number; flightSeconds: number }>();

  for (const item of recentItems) {
    if (item.pilot_uid) {
      uniquePilots.add(item.pilot_uid);
      const ep = pilotCounts.get(item.pilot_uid);
      if (ep) { ep.count++; } else { pilotCounts.set(item.pilot_uid, { uid: item.pilot_uid, name: item.pilot_name || item.pilot_uid, count: 1, kills: 0, flightSeconds: 0 }); }
    }
    if (item.event_type === "kill" && item.pilot_uid) {
      killCount++;
      const ep = pilotCounts.get(item.pilot_uid);
      if (ep) ep.kills++;
    }
    if (item.event_type === "landing" && item.metadata.duration_seconds) {
      flightSeconds += item.metadata.duration_seconds;
      if (item.pilot_uid) {
        const ep = pilotCounts.get(item.pilot_uid);
        if (ep) ep.flightSeconds += item.metadata.duration_seconds;
      }
    }
    if (item.kind === "proficiency") proficienciesEarned++;
    if (item.kind === "achievement") achievementsEarned++;
  }

  let topPilot: WeeklyStats["topPilot"] = null;
  let topCount = 0;
  for (const pilot of pilotCounts.values()) {
    if (pilot.count > topCount) {
      topCount = pilot.count;
      topPilot = { uid: pilot.uid, name: pilot.name, killCount: pilot.kills, flightHours: pilot.flightSeconds / 3600 };
    }
  }

  const ninetySecondsAgo = new Date(Date.now() - 90 * 1000);
  const hasAnyHeartbeat = servers.some((s) => s.last_seen_at !== null);

  const serverStats: ServerStat[] = hasAnyHeartbeat
    ? servers
        .map((s) => {
          const isOnline = s.last_seen_at !== null && new Date(s.last_seen_at) >= ninetySecondsAgo;
          return { name: s.name, isOnline, playerCount: isOnline ? (s.player_count ?? null) : null };
        })
        .sort((a, b) => {
          if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
          return (b.playerCount ?? -1) - (a.playerCount ?? -1);
        })
    : [];

  return {
    activePilots: uniquePilots.size,
    killCount,
    flightHours: flightSeconds / 3600,
    proficienciesEarned,
    achievementsEarned,
    servers: serverStats,
    topPilot,
  };
}

export default function WeeklyStatsBar({ stats, loading, personal, showServers: allowServers = true }: { stats: WeeklyStats | null; loading: boolean; personal: boolean; showServers?: boolean }) {
  if (loading && !stats) {
    return <section className="panel dashboard-card-wide this-week-banner" />;
  }

  const s = stats ?? { activePilots: 0, killCount: 0, flightHours: 0, proficienciesEarned: 0, achievementsEarned: 0, servers: [], topPilot: null };
  const showServers = allowServers && s.servers.length > 0;
  const visibleServers = s.servers.slice(0, SERVER_LIST_MAX);
  const hiddenCount = s.servers.length - visibleServers.length;

  const quiet = personal && s.killCount === 0 && s.flightHours === 0 && s.proficienciesEarned === 0 && s.achievementsEarned === 0;

  return (
    <section className="panel dashboard-card-wide this-week-banner">
      <div className="this-week-section">
        <span className="this-week-section-label">Past 7 Days</span>
        {quiet ? <p className="this-week-quiet">No activity in the past 7 days.</p> : <div className="this-week-stats-row">
          {!personal && (

            <div className="this-week-stat">
              <span className="this-week-value">{s.activePilots}</span>
              <span className="this-week-label">Active Pilots</span>
            </div>
          )}
          <div className="this-week-stat">
            <span className="this-week-value">{s.killCount}</span>
            <span className="this-week-label">Kills</span>
          </div>
          <div className="this-week-stat">
            <span className="this-week-value">{s.flightHours >= 1 ? Math.round(s.flightHours) : s.flightHours > 0 ? s.flightHours.toFixed(1) : "0"}</span>
            <span className="this-week-label">Flight Hrs</span>
          </div>
          <div className="this-week-stat">
            <span className="this-week-value">{s.proficienciesEarned}</span>
            <span className="this-week-label">Proficiencies</span>
          </div>
          <div className="this-week-stat">
            <span className="this-week-value">{s.achievementsEarned}</span>
            <span className="this-week-label">Achievements</span>
          </div>
        </div>}
      </div>

      {showServers && (
        <>
          <div className="this-week-divider" aria-hidden="true" />
          <div className="this-week-section this-week-section-narrow">
            <span className="this-week-section-label">Servers</span>
            <div className="this-week-servers">
              {visibleServers.map((server) => (
                <div key={server.name} className="this-week-server-row">
                  <span className={`this-week-server-dot ${server.isOnline ? "is-online" : "is-offline"}`} aria-hidden="true" />
                  <span className="this-week-server-name">{server.name}</span>
                  {server.isOnline && server.playerCount !== null && (
                    <span className="this-week-server-count">({server.playerCount})</span>
                  )}
                </div>
              ))}
              {hiddenCount > 0 && (
                <span className="this-week-server-more">and {hiddenCount} more</span>
              )}
            </div>
          </div>
        </>
      )}

      {!personal && s.topPilot && (
        <>
          <div className="this-week-divider" aria-hidden="true" />
          <div className="this-week-section this-week-section-narrow">
            <span className="this-week-section-label">Most Active</span>
            <div className="this-week-spotlight">
              <a
                className="this-week-spotlight-name"
                href={`#/pilots/${encodeURIComponent(s.topPilot.uid)}`}
              >
                {s.topPilot.name}
              </a>
              <span className="this-week-spotlight-sub">
                {s.topPilot.flightHours >= 0.1 ? `${s.topPilot.flightHours < 1 ? s.topPilot.flightHours.toFixed(1) : Math.round(s.topPilot.flightHours)}h` : null}
                {s.topPilot.flightHours >= 0.1 && s.topPilot.killCount > 0 ? " · " : null}
                {s.topPilot.killCount > 0 ? `${s.topPilot.killCount} ${s.topPilot.killCount === 1 ? "kill" : "kills"}` : null}
                {s.topPilot.flightHours < 0.1 && s.topPilot.killCount === 0 ? "No stats in past 7 days" : null}
              </span>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

